import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Check, CheckCheck, FileText, DollarSign, AlertTriangle, LogOut, ArrowLeft } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";

const ICON_MAP = {
  report_submitted: FileText,
  payment_recorded: DollarSign,
  invoice_overdue: AlertTriangle,
};

const COLOR_MAP = {
  report_submitted: "text-emerald-400",
  payment_recorded: "text-blue-400",
  invoice_overdue: "text-orange-400",
};

const BG_MAP = {
  report_submitted: "bg-emerald-500/10 border-emerald-500/20",
  payment_recorded: "bg-blue-500/10 border-blue-500/20",
  invoice_overdue: "bg-orange-500/10 border-orange-500/20",
};

export default function NotificationsPage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);
  const limit = 30;

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/notifications?limit=${limit}&offset=${offset}`);
      setNotifications(data.notifications);
      setTotal(data.total);
    } catch {}
    setLoading(false);
  }, [offset]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const filtered = notifications.filter((n) => {
    if (filter === "unread") return !n.read;
    if (filter === "report_submitted" || filter === "payment_recorded" || filter === "invoice_overdue") return n.type === filter;
    return true;
  });

  const goBack = () => {
    if (user?.role === "admin") navigate("/admin");
    else if (user?.role === "viewer") navigate("/reports");
    else navigate("/home");
  };

  return (
    <div className="min-h-screen" data-testid="notifications-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout}><LogOut className="h-4 w-4" strokeWidth={1.5} /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" size="sm" className="mb-6 text-muted-foreground" onClick={goBack} data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Back
        </Button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-headline text-2xl font-bold tracking-tight flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" strokeWidth={1.5} /> Notifications
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              <span className="kpi-value">{total}</span> total
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40 bg-background border-border text-sm" data-testid="notification-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="report_submitted">Reports</SelectItem>
                <SelectItem value="payment_recorded">Payments</SelectItem>
                <SelectItem value="invoice_overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleMarkAllRead} className="border-border text-xs" data-testid="mark-all-read-page">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading...</div>
        ) : filtered.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center text-muted-foreground text-sm">
              <Bell className="h-10 w-10 mx-auto mb-3 opacity-20" />
              {filter === "all" ? "No notifications yet" : "No matching notifications"}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((n) => {
              const Icon = ICON_MAP[n.type] || Bell;
              const colorClass = COLOR_MAP[n.type] || "text-muted-foreground";
              const bgClass = BG_MAP[n.type] || "bg-card border-border";
              const timeAgo = formatDistanceToNow(new Date(n.created_at), { addSuffix: true });

              return (
                <Card key={n.id} className={`border transition-all ${n.read ? "border-border bg-card opacity-70" : bgClass}`} data-testid={`notif-card-${n.id}`}>
                  <CardContent className="py-3 px-4 flex items-start gap-3">
                    <div className={`mt-0.5 ${colorClass}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{n.title}</p>
                        {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1.5 font-mono">
                        {timeAgo} — {format(new Date(n.created_at), "MMM d, yyyy h:mm a")}
                      </p>
                    </div>
                    {!n.read && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => handleMarkRead(n.id)}>
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} className="border-border">
              Previous
            </Button>
            <span className="text-sm text-muted-foreground self-center">
              {offset + 1}-{Math.min(offset + limit, total)} of {total}
            </span>
            <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} className="border-border">
              Next
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
